import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, type SeasonSummary } from '../../api'
import { actionButton } from '../ui/variants'
import { CareerTable } from './Standings'

/**
 * One fantasy league: its seasons, and its all-time table.
 *
 * A season is a set of tournaments plus a window, so creating one is the manager's job and needs a
 * tournament picker rather than a text field — which is why the create form searches `/api/leagues`
 * by name and shows dates and match counts. TI 2026 is six league ids that all look alike in a list.
 */
export function SeasonList({ leagueId, go }: {
  leagueId: number
  go: (...segments: (string | number)[]) => void
}) {
  const [creating, setCreating] = useState(false)

  const seasons = useQuery({
    queryKey: ['fantasy', 'seasons', leagueId],
    queryFn: () => api.seasons(leagueId),
  })

  const mine = useQuery({ queryKey: ['fantasy', 'mine'], queryFn: api.myFantasyLeagues })
  const league = (mine.data ?? []).find((entry) => entry.id === leagueId)
  const isManager = league?.role === 'manager'

  return (
    <div className="space-y-6">
      <div>
        <button
          type="button"
          onClick={() => go()}
          className="text-xs text-ink-faint hover:text-ink"
        >
          ← All leagues
        </button>
        <h2 className="mt-2 text-2xl font-semibold">{league?.name ?? 'League'}</h2>
      </div>

      {isManager ? (
        <InviteBox leagueId={leagueId} />
      ) : null}

      <section className="space-y-2">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h3 className="text-sm font-medium text-ink-soft">Seasons</h3>
          {isManager && !creating ? (
            <button
              type="button"
              onClick={() => setCreating(true)}
              className={actionButton()}
            >
              New season
            </button>
          ) : null}
        </div>

        {creating ? (
          <CreateSeasonForm leagueId={leagueId} onDone={() => setCreating(false)} />
        ) : null}

        {seasons.isPending ? (
          <p className="text-sm text-ink-faint">Loading…</p>
        ) : (seasons.data ?? []).length === 0 ? (
          <p className="rounded border border-line bg-surface px-4 py-8 text-center text-sm text-ink-soft">
            {isManager
              ? 'No seasons yet. Create one and pick the tournaments it follows.'
              : 'No seasons yet. The manager of this league has not started one.'}
          </p>
        ) : (
          <div className="grid gap-2 sm:grid-cols-2">
            {(seasons.data ?? []).map((season) => (
              <SeasonCard
                key={season.id}
                leagueId={leagueId}
                season={season}
                isManager={isManager}
                onOpen={() => go(leagueId, season.id)}
              />
            ))}
          </div>
        )}
      </section>

      <CareerTable leagueId={leagueId} />
    </div>
  )
}

function SeasonCard({
  leagueId,
  season,
  isManager,
  onOpen,
}: {
  leagueId: number
  season: SeasonSummary
  isManager: boolean
  onOpen: () => void
}) {
  const [editing, setEditing] = useState(false)

  if (editing) {
    return <EditSeasonForm leagueId={leagueId} season={season} onDone={() => setEditing(false)} />
  }

  /*
   * The card is a button, so the edit control cannot be nested inside it — a button in a button is
   * invalid and the inner click is swallowed. The whole thing is a div with the card body as its
   * own button beside a separate one, which also keeps the two tap targets apart on a phone.
   */
  return (
    <div className="min-w-0 rounded-lg border border-line bg-surface transition-colors focus-within:border-accent-edge hover:border-accent-edge">
      <button
        type="button"
        onClick={onOpen}
        className="min-w-0 w-full p-3 text-left"
      >
        <div className="flex items-start justify-between gap-2">
          <span className="min-w-0 truncate text-sm">{season.name}</span>
          <StatusTag status={season.status} />
        </div>
        <div className="tabular mt-1 text-xs text-ink-faint">
          {season.entries} entered · {season.tournaments}{' '}
          {season.tournaments === 1 ? 'tournament' : 'tournaments'}
          {season.joined ? ' · you are in' : ''}
        </div>
        <div className="tabular mt-1 text-xs text-ink-faint">
          {formatWindow(season.opensAt, season.closesAt)}
        </div>
      </button>

      {isManager ? (
        <div className="border-t border-line px-3 py-1.5">
          <button
            type="button"
            onClick={() => setEditing(true)}
            className="min-h-11 text-xs text-ink-soft underline decoration-dotted underline-offset-2 sm:min-h-0"
          >
            Edit season
          </button>
        </div>
      ) : null}
    </div>
  )
}

/**
 * The season's window, on the card.
 *
 * Shown because it is the thing most likely to be wrong and was invisible: the closing date decides
 * which fixtures the season offers, when bets stop being accepted, and how hard the Draft Master
 * stakes. A manager cannot notice a wrong date they cannot see.
 */
function formatWindow(opensAt: string, closesAt: string): string {
  const day = (iso: string) =>
    new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short' })

  return `${day(opensAt)} – ${day(closesAt)}`
}

function StatusTag({ status }: { status: SeasonSummary['status'] }) {
  const styles: Record<SeasonSummary['status'], string> = {
    draft: 'bg-surface-raised text-ink-faint',
    open: 'bg-accent-bg text-accent',
    finished: 'bg-surface-raised text-ink-soft',
  }

  return (
    <span className={`shrink-0 rounded px-1.5 py-0.5 text-[10px] uppercase tracking-wide ${styles[status]}`}>
      {status}
    </span>
  )
}

/** A link into a league nobody outside it can see. Shown once and stored only as a hash. */
function InviteBox({ leagueId }: { leagueId: number }) {
  const [token, setToken] = useState<string | null>(null)

  const create = useMutation({
    mutationFn: () => api.createInvite(leagueId, null, null),
    onSuccess: (invite) => setToken(invite.token),
  })

  return (
    <div className="min-w-0 rounded-lg border border-line bg-surface p-4">
      <h3 className="text-sm font-medium">Invite someone</h3>
      {token === null ? (
        <>
          <p className="mt-1 text-xs text-ink-faint">
            They paste the token on the fantasy page to join.
          </p>
          <button
            type="button"
            onClick={() => create.mutate()}
            disabled={create.isPending}
            className={actionButton({ className: 'mt-3' })}
          >
            Make an invite
          </button>
        </>
      ) : (
        <>
          <p className="mt-1 text-xs text-ink-faint">
            Shown once — only its hash is stored, so it cannot be recovered afterwards.
          </p>
          <code className="mt-2 block overflow-x-auto rounded bg-surface-raised px-2 py-1 text-xs">
            {token}
          </code>
        </>
      )}
    </div>
  )
}

/**
 * Creating a season: a name, a window, and the tournaments it follows.
 *
 * The tournament picker searches by name because a tournament is not one league id — TI 2026 is the
 * main event plus five regional qualifiers, all spelled almost identically — so dates and match
 * counts are what tell the candidates apart.
 */
/**
 * Change a season after it exists.
 *
 * Every field the API accepts was patchable from the moment the endpoint shipped, and none of it
 * was reachable — so a season's closing date, the one thing most likely to be wrong, could only be
 * corrected by hand in the database. A tournament's end date is not knowable when the season is
 * created; the bracket decides it.
 *
 * The window is not cosmetic. `SeasonFixtures` will not show a fixture outside it, `BetPlacer`
 * refuses a bet on one, and the Draft Master reads the closing date to decide how hard to stake —
 * it holds back early in a season and presses as the end approaches. A season whose dates are wrong
 * is a season that quietly offers the wrong fixtures and bets the wrong size.
 *
 * Only the season's own attributes. Which tournaments it follows needs the league picker the create
 * form carries, and changing that after people have bet on fixtures it would remove is a different
 * question from correcting a date — it stays where it is until somebody asks for it.
 */
function EditSeasonForm({
  leagueId,
  season,
  onDone,
}: {
  leagueId: number
  season: SeasonSummary
  onDone: () => void
}) {
  const queryClient = useQueryClient()

  // A date input wants a local YYYY-MM-DD and the API speaks instants, so the two conversions have
  // to be inverses. Read back through the browser's own timezone, exactly as the create form writes
  // them, or a season edited without touching the dates would silently shift by a day.
  const asDay = (iso: string) => {
    const at = new Date(iso)
    const local = new Date(at.getTime() - at.getTimezoneOffset() * 60_000)
    return local.toISOString().slice(0, 10)
  }

  const [name, setName] = useState(season.name)
  const [opensAt, setOpensAt] = useState(() => asDay(season.opensAt))
  const [closesAt, setClosesAt] = useState(() => asDay(season.closesAt))
  const [status, setStatus] = useState(season.status)
  const [includeBots, setIncludeBots] = useState(season.includeBots)
  const [error, setError] = useState<string | null>(null)

  const save = useMutation({
    mutationFn: () =>
      api.updateSeason(leagueId, season.id, {
        name: name.trim(),
        opensAt: new Date(`${opensAt}T00:00:00`).toISOString(),
        // End of day, matching how the create form writes a closing date: a season closing "Sunday"
        // means the end of Sunday, not the start of it.
        closesAt: new Date(`${closesAt}T23:59:59`).toISOString(),
        status,
        includeBots,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['fantasy', 'seasons', leagueId] })
      void queryClient.invalidateQueries({ queryKey: ['fantasy', 'season', leagueId, season.id] })
      onDone()
    },
    onError: (failure: Error) => setError(failure.message),
  })

  return (
    <div className="min-w-0 space-y-3 rounded-lg border border-accent-edge bg-surface p-4">
      <input
        value={name}
        onChange={(event) => setName(event.target.value)}
        aria-label="Season name"
        className="min-h-11 w-full rounded border border-line bg-surface-raised px-2 text-sm text-ink"
      />

      <div className="flex flex-wrap gap-2 text-xs text-ink-soft">
        <label className="flex items-center gap-2">
          Opens
          <input
            type="date"
            value={opensAt}
            onChange={(event) => setOpensAt(event.target.value)}
            className="min-h-11 rounded border border-line bg-surface-raised px-2 text-ink sm:min-h-0 sm:py-1"
          />
        </label>
        <label className="flex items-center gap-2">
          Closes
          <input
            type="date"
            value={closesAt}
            onChange={(event) => setClosesAt(event.target.value)}
            className="min-h-11 rounded border border-line bg-surface-raised px-2 text-ink sm:min-h-0 sm:py-1"
          />
        </label>
      </div>

      <div className="flex flex-wrap items-center gap-3 text-xs text-ink-soft">
        <label className="flex items-center gap-2">
          Status
          <select
            value={status}
            onChange={(event) => setStatus(event.target.value as SeasonSummary['status'])}
            className="min-h-11 rounded border border-line bg-surface-raised px-2 text-ink sm:min-h-0 sm:py-1"
          >
            <option value="draft">draft</option>
            <option value="open">open</option>
            <option value="finished">finished</option>
          </select>
        </label>
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={includeBots}
            onChange={(event) => setIncludeBots(event.target.checked)}
            className="size-4"
          />
          House characters play
        </label>
      </div>

      {/*
        Said before the button rather than after the mistake. A finished season cannot be reopened —
        its fixtures have results, so anything bet into it now would be bet with hindsight — and the
        API refuses it. A manager should know that before choosing, not from an error.
      */}
      {season.status === 'finished' ? (
        <p className="text-xs text-ink-faint">
          This season is finished and cannot be reopened: its fixtures have results, so anything bet
          into it now would be bet with hindsight.
        </p>
      ) : null}

      {error ? <p className="text-xs text-danger">{error}</p> : null}

      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={() => {
            setError(null)
            save.mutate()
          }}
          disabled={save.isPending || name.trim().length === 0}
          className="min-h-11 rounded bg-accent px-3 text-sm font-medium text-accent-ink disabled:opacity-50"
        >
          {save.isPending ? 'Saving…' : 'Save changes'}
        </button>
        <button
          type="button"
          onClick={onDone}
          className="min-h-11 rounded border border-line px-3 text-sm text-ink-soft"
        >
          Cancel
        </button>
      </div>
    </div>
  )
}

function CreateSeasonForm({ leagueId, onDone }: { leagueId: number; onDone: () => void }) {
  const queryClient = useQueryClient()
  const [name, setName] = useState('')
  const [query, setQuery] = useState('')
  const [picked, setPicked] = useState<number[]>([])
  const [opensAt, setOpensAt] = useState(() => new Date().toISOString().slice(0, 10))
  const [closesAt, setClosesAt] = useState(() =>
    new Date(Date.now() + 30 * 86_400_000).toISOString().slice(0, 10),
  )
  const [error, setError] = useState<string | null>(null)

  const leagues = useQuery({
    queryKey: ['leagues', 'search', query],
    queryFn: () => api.leagues(query.trim().length > 0 ? query.trim() : undefined),
  })

  const create = useMutation({
    mutationFn: () =>
      api.createSeason(leagueId, {
        name: name.trim(),
        // Dates come out of a date input as a local day; the API wants an instant, and the season
        // window is judged on kick-off times which are stored UTC.
        opensAt: new Date(`${opensAt}T00:00:00`).toISOString(),
        closesAt: new Date(`${closesAt}T23:59:59`).toISOString(),
        leagueIds: picked,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['fantasy', 'seasons', leagueId] })
      onDone()
    },
    onError: (failure: Error) => setError(failure.message),
  })

  const results = (leagues.data ?? []).filter((entry) => entry.matchCount > 0).slice(0, 12)

  return (
    <div className="min-w-0 space-y-3 rounded-lg border border-line bg-surface p-4">
      <input
        value={name}
        onChange={(event) => setName(event.target.value)}
        placeholder="Season name, e.g. The International 2026"
        className="min-h-11 w-full rounded border border-line bg-surface-raised px-2 text-sm text-ink"
      />

      <div className="flex flex-wrap gap-2 text-xs text-ink-soft">
        <label className="flex items-center gap-2">
          Opens
          <input
            type="date"
            value={opensAt}
            onChange={(event) => setOpensAt(event.target.value)}
            className="min-h-11 rounded border border-line bg-surface-raised px-2 text-ink sm:min-h-0 sm:py-1"
          />
        </label>
        <label className="flex items-center gap-2">
          Closes
          <input
            type="date"
            value={closesAt}
            onChange={(event) => setClosesAt(event.target.value)}
            className="min-h-11 rounded border border-line bg-surface-raised px-2 text-ink sm:min-h-0 sm:py-1"
          />
        </label>
      </div>

      <div>
        <p className="text-xs text-ink-faint">
          Pick every league the tournament runs under. TI 2026 is the main event plus five regional
          qualifiers, and a fixture in a league you leave out is not bettable.
        </p>
        <input
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Search tournaments"
          className="mt-2 min-h-11 w-full rounded border border-line bg-surface-raised px-2 text-sm text-ink"
        />

        <div className="mt-2 max-h-64 space-y-1 overflow-y-auto">
          {results.map((entry) => {
            const chosen = picked.includes(entry.leagueId)
            return (
              <button
                key={entry.leagueId}
                type="button"
                onClick={() =>
                  setPicked(
                    chosen
                      ? picked.filter((id) => id !== entry.leagueId)
                      : [...picked, entry.leagueId],
                  )
                }
                className={`flex w-full min-w-0 items-center justify-between gap-2 rounded border px-2 py-2 text-left text-xs ${
                  chosen ? 'border-accent-edge bg-accent-bg' : 'border-line bg-surface-raised'
                }`}
              >
                <span className="min-w-0 truncate">{entry.name ?? `League ${entry.leagueId}`}</span>
                <span className="tabular shrink-0 text-ink-faint">
                  {entry.matchCount} · {formatRange(entry.firstMatch, entry.lastMatch)}
                </span>
              </button>
            )
          })}
        </div>
      </div>

      {error !== null ? <p className="text-xs text-danger">{error}</p> : null}

      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          disabled={name.trim().length < 3 || picked.length === 0 || create.isPending}
          onClick={() => create.mutate()}
          className={actionButton()}
        >
          Create as draft
        </button>
        <button
          type="button"
          onClick={onDone}
          className="min-h-11 rounded px-3 text-sm text-ink-faint hover:text-ink"
        >
          Cancel
        </button>
      </div>

      <p className="text-xs text-ink-faint">
        It starts as a draft, visible only to you. Open it when the tournaments are right.
      </p>
    </div>
  )
}

function formatRange(first: string | null | undefined, last: string | null | undefined): string {
  if (first === null || first === undefined) return 'no matches'
  const day = (value: string) => new Date(value).toISOString().slice(0, 10)
  return last === null || last === undefined || day(first) === day(last)
    ? day(first)
    : `${day(first)} → ${day(last)}`
}
